"use client";

import { Link } from "@/components/LocaleLink";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles,
  Mic,
  FileSearch,
  BarChart3,
  Shield,
  Check,
  X,
  ArrowRight,
  Globe,
  Briefcase,
  GraduationCap,
  Building2,
  Zap,
  Lock,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Footer } from "@/components/Footer";
import { useTranslation } from "@/lib/hooks/useTranslation";

const t = {
  fr: {
    nav: {
      login: "Connexion",
      ctaLong: "Commencer gratuit",
      ctaShort: "S'inscrire",
    },
    hero: {
      badge: "Plateforme IA française de préparation aux entretiens",
      titleLine1: "La plateforme IA de préparation",
      titleLine2: "aux entretiens, en français",
      description: (
        <>
          <strong className="text-foreground">Velyq</strong> est la plateforme
          IA francophone qui simule tes entretiens d&apos;embauche, analyse ton
          CV contre les ATS et te donne un feedback chiffré à chaque session.
          Gratuit sans carte bancaire, Premium 9,99 €/mois pour débloquer
          l&apos;écosystème complet (entretiens vocaux illimités, modes
          sectoriels, CV Builder, tracker candidatures).
        </>
      ),
      ctaPrimary: "Commencer gratuit",
      ctaSecondary: "Voir les tarifs",
      trustNoCard: "Sans carte bancaire",
      trustHosting: "Hébergement européen · RGPD",
      trustLang: "FR & EN",
    },
    whyFR: {
      badge: "Pourquoi une plateforme française",
      title: "Pensée pour le marché français, pas adaptée",
      subtitle:
        "Les outils américains sont brillants, mais calibrés sur les codes US : storytelling Why-Us, CV 1 page, case coding FAANG. Velyq part des standards français et les assume.",
      cards: [
        {
          tag: "ÉCOLES & SECTEURS",
          title: "Les repères FR",
          desc:
            "HEC, Polytechnique, Dauphine, Sciences Po, MBB France, BFI Paris, scale-ups Doctolib/Qonto/Alan, PME régionales. Les questions et grilles de correction collent à ces codes.",
        },
        {
          tag: "ATS FRANÇAIS",
          title: "Offres FR incluses",
          desc:
            "HelloWork, Cadremploi, APEC, France Travail, WTTJ. L'analyse CV lit les offres dans leur format natif et extrait les mots-clés réellement pondérés par les ATS FR.",
        },
        {
          tag: "LANGUE & CULTURE",
          title: "Feedback en VO",
          desc:
            "Correction de l'anglais technique si tu postules à l'étranger, mais tout le reste (questions, feedback, rapports PDF) en français natif. Pas de mauvaise traduction, pas d'anglicismes forcés.",
        },
      ],
    },
    pillars: {
      badge: "Fonctionnalités clés",
      title: "Tout l'écosystème carrière en un seul outil",
      subtitle:
        "Entretien, CV, lettre de motivation, tracker candidatures : un seul compte, une seule interface, un seul abonnement.",
      items: [
        {
          title: "Simulateur vocal IA",
          description:
            "Tu parles à voix haute, l'IA répond et te relance comme un vrai recruteur. Feedback sur le fond et la forme à la fin.",
        },
        {
          title: "Analyse CV ATS",
          description:
            "Score contre l'offre cible en 30 secondes. Mots-clés manquants, titre de poste, format parsable Workday/Greenhouse.",
        },
        {
          title: "Feedback chiffré",
          description:
            "Rapport PDF après chaque session : clarté, structure STAR, tics de langage, temps de parole, pistes d'amélioration.",
        },
        {
          title: "Modes sectoriels",
          description:
            "Tech (FAANG, scale-ups FR), finance (BFI, PE, M&A), consulting (MBB, stratégie) — chaque mode a sa grille calibrée.",
        },
        {
          title: "Bilingue natif",
          description:
            "FR et EN, bascule à la volée. L'IA corrige aussi ton anglais technique si tu vises Google, Meta ou Stripe.",
        },
        {
          title: "Hébergement européen",
          description:
            "GCP europe-west1, conforme RGPD. Audio chiffré, supprimable à la demande. Rien n'est revendu.",
        },
      ],
    },
    comparison: {
      badge: "Comparatif",
      title: "Velyq vs Huru vs Final Round AI vs Yoodli",
      subtitle:
        "Où on gagne, où on perd. Transparence sur les différences — choisis l'outil qui colle à ton cas.",
      columns: {
        criterion: "Critère",
      },
      rows: [
        "Langue native FR (interface, questions, feedback)",
        "Simulateur vocal temps réel",
        "Analyse CV ATS intégrée",
        "Modes sectoriels (tech, finance, consulting)",
        "Live copilot pendant l'entretien (éthiquement risqué)",
        "Écosystème carrière (CV, LM, tracker)",
        "Prix Premium FR",
      ],
      pricing: {
        velyq: "9,99 €/mois",
        huru: "24,99 €/mois",
        finalRound: "~65 $/mois",
        yoodli: "~20 $/mois",
      },
      ethicsNote: "Positionnement éthique de Velyq : préparation en amont, pas de triche en direct. ",
      ethicsLink: "Pourquoi on n'a pas de live copilot.",
    },
    useCases: {
      badge: "Pour qui",
      title: "Trois profils, un seul outil",
      items: [
        {
          title: "Étudiants & jeunes diplômés",
          description:
            "Stage, premier CDI, alternance. Entraîne-toi aux 10 questions classiques + méthode STAR avant ton premier entretien RH.",
          cta: "Préparer mon premier entretien",
        },
        {
          title: "Candidats actifs",
          description:
            "10+ candidatures/mois ? Analyse chaque offre, calibre ton CV, entraîne-toi 20 min avant chaque entretien prévu.",
          cta: "Optimiser mes candidatures",
        },
        {
          title: "Reconversion & transition",
          description:
            "Changement de secteur, retour à l'emploi, évolution interne. Modes spécialisés pour raconter une trajectoire non-linéaire.",
          cta: "Préparer ma reconversion",
        },
      ],
    },
    resources: {
      badge: "Ressources",
      title: "Approfondis avant de t'inscrire",
      cards: [
        {
          tag: "MODE TECH",
          title: "Coding + system design",
          desc:
            "FAANG, scale-ups FR. Python, JS, Go, Rust. Grilles de correction calibrées Google / Meta / Doctolib.",
          cta: "EXPLORER →",
        },
        {
          tag: "MODE FINANCE",
          title: "DCF, LBO, M&A, brain teasers",
          desc:
            "BFI Paris, PE, asset management, Big 4 audit. Questions calibrées Goldman / Rothschild / Lazard.",
          cta: "EXPLORER →",
        },
        {
          tag: "MODE CONSULTING",
          title: "Case studies MBB",
          desc:
            "McKinsey, BCG, Bain France. Profitability, market sizing, M&A, frameworks MECE.",
          cta: "EXPLORER →",
        },
      ],
      alsoLabel: "Et aussi :",
      atsBadge: "Analyse CV ATS",
      aiBadge: "L'IA peut-elle vraiment t'aider ?",
      altWarmupBadge: "Alternative Interview Warmup",
      blogBadge: "Tous les articles",
    },
    cta: {
      title: "Prêt à t'entraîner sérieusement ?",
      subtitle:
        "Inscription gratuite, sans carte bancaire. Premium 9,99 €/mois débloqué à la minute où tu en as besoin.",
      button: "Commencer gratuitement",
      disclaimer: "Sans carte bancaire · Hébergement européen · RGPD",
    },
  },
  en: {
    nav: {
      login: "Login",
      ctaLong: "Start free",
      ctaShort: "Sign up",
    },
    hero: {
      badge: "French AI platform for interview prep",
      titleLine1: "The AI platform to prep",
      titleLine2: "for interviews, in French and English",
      description: (
        <>
          <strong className="text-foreground">Velyq</strong> is the French-built
          AI platform that simulates your job interviews, analyzes your resume
          against ATS systems and gives you quantified feedback at every
          session. Free, no credit card. Premium at €9.99/month to unlock the
          full ecosystem (unlimited voice interviews, sector modes, Resume
          Builder, application tracker).
        </>
      ),
      ctaPrimary: "Start free",
      ctaSecondary: "View pricing",
      trustNoCard: "No credit card",
      trustHosting: "European hosting · GDPR",
      trustLang: "FR & EN",
    },
    whyFR: {
      badge: "Why a French platform",
      title: "Built for the French market, not adapted",
      subtitle:
        "US tools are brilliant, but calibrated on US codes: Why-Us storytelling, 1-page resume, FAANG coding cases. Velyq starts from French standards and owns them.",
      cards: [
        {
          tag: "SCHOOLS & SECTORS",
          title: "FR reference points",
          desc:
            "HEC, Polytechnique, Dauphine, Sciences Po, MBB France, IB Paris, scale-ups Doctolib/Qonto/Alan, regional SMEs. Questions and scoring grids fit these codes.",
        },
        {
          tag: "FRENCH ATS",
          title: "FR job postings included",
          desc:
            "HelloWork, Cadremploi, APEC, France Travail, WTTJ. The resume analyzer reads job postings in their native format and pulls the keywords actually weighted by FR ATS systems.",
        },
        {
          tag: "LANGUAGE & CULTURE",
          title: "Native feedback",
          desc:
            "We'll correct your technical English if you're applying abroad, but everything else (questions, feedback, PDF reports) is in native French. No bad translations, no forced anglicisms.",
        },
      ],
    },
    pillars: {
      badge: "Key features",
      title: "The full career ecosystem in one tool",
      subtitle:
        "Interview, resume, cover letter, application tracker: one account, one interface, one subscription.",
      items: [
        {
          title: "AI voice simulator",
          description:
            "You speak out loud, the AI responds and challenges you like a real recruiter. Feedback on substance and form at the end.",
        },
        {
          title: "ATS resume analysis",
          description:
            "Score against the target offer in 30 seconds. Missing keywords, job title, Workday/Greenhouse-parsable format.",
        },
        {
          title: "Quantified feedback",
          description:
            "PDF report after every session: clarity, STAR structure, filler words, speaking time, improvement leads.",
        },
        {
          title: "Sector modes",
          description:
            "Tech (FAANG, FR scale-ups), finance (IB, PE, M&A), consulting (MBB, strategy) — each mode has its calibrated grid.",
        },
        {
          title: "Native bilingual",
          description:
            "FR and EN, switch on the fly. The AI also corrects your technical English if you're targeting Google, Meta or Stripe.",
        },
        {
          title: "European hosting",
          description:
            "GCP europe-west1, GDPR-compliant. Encrypted audio, deletable on request. Nothing resold.",
        },
      ],
    },
    comparison: {
      badge: "Comparison",
      title: "Velyq vs Huru vs Final Round AI vs Yoodli",
      subtitle:
        "Where we win, where we lose. Transparency on differences — pick the tool that fits your case.",
      columns: {
        criterion: "Criterion",
      },
      rows: [
        "Native FR language (interface, questions, feedback)",
        "Real-time voice simulator",
        "Built-in ATS resume analysis",
        "Sector modes (tech, finance, consulting)",
        "Live copilot during the interview (ethically risky)",
        "Career ecosystem (resume, CL, tracker)",
        "FR Premium price",
      ],
      pricing: {
        velyq: "€9.99/mo",
        huru: "€24.99/mo",
        finalRound: "~$65/mo",
        yoodli: "~$20/mo",
      },
      ethicsNote: "Velyq's ethical stance: prep upfront, no live cheating. ",
      ethicsLink: "Why we don't have a live copilot.",
    },
    useCases: {
      badge: "Who it's for",
      title: "Three profiles, one tool",
      items: [
        {
          title: "Students & new graduates",
          description:
            "Internship, first job, apprenticeship. Practice the 10 classic questions + the STAR method before your first HR interview.",
          cta: "Prep my first interview",
        },
        {
          title: "Active job seekers",
          description:
            "10+ applications a month? Analyze each offer, calibrate your resume, train 20 min before every scheduled interview.",
          cta: "Optimize my applications",
        },
        {
          title: "Career change & transition",
          description:
            "Sector switch, return to work, internal evolution. Specialized modes to tell a non-linear story.",
          cta: "Prep my career change",
        },
      ],
    },
    resources: {
      badge: "Resources",
      title: "Dive deeper before signing up",
      cards: [
        {
          tag: "TECH MODE",
          title: "Coding + system design",
          desc:
            "FAANG, FR scale-ups. Python, JS, Go, Rust. Scoring grids calibrated Google / Meta / Doctolib.",
          cta: "EXPLORE →",
        },
        {
          tag: "FINANCE MODE",
          title: "DCF, LBO, M&A, brain teasers",
          desc:
            "IB Paris, PE, asset management, Big 4 audit. Questions calibrated Goldman / Rothschild / Lazard.",
          cta: "EXPLORE →",
        },
        {
          tag: "CONSULTING MODE",
          title: "MBB case studies",
          desc:
            "McKinsey, BCG, Bain France. Profitability, market sizing, M&A, MECE frameworks.",
          cta: "EXPLORE →",
        },
      ],
      alsoLabel: "Also see:",
      atsBadge: "ATS resume analysis",
      aiBadge: "Can AI really help you?",
      altWarmupBadge: "Alternative Interview Warmup",
      blogBadge: "All articles",
    },
    cta: {
      title: "Ready to train seriously?",
      subtitle:
        "Free signup, no credit card. Premium €9.99/month unlocked the minute you need it.",
      button: "Start for free",
      disclaimer: "No credit card · European hosting · GDPR",
    },
  },
};

const pillarIcons = [Mic, FileSearch, BarChart3, Briefcase, Globe, Shield];
const useCaseIcons = [GraduationCap, Briefcase, Building2];

// Comparison row config: shared structure (booleans + pricing) decoupled from labels
const comparisonRowsBase: Array<{
  velyq: boolean | "pricing";
  huru: boolean | "pricing";
  finalRound: boolean | "pricing";
  yoodli: boolean | "pricing";
}> = [
  { velyq: true, huru: true, finalRound: false, yoodli: false },
  { velyq: true, huru: true, finalRound: true, yoodli: true },
  { velyq: true, huru: false, finalRound: false, yoodli: false },
  { velyq: true, huru: true, finalRound: true, yoodli: false },
  { velyq: false, huru: false, finalRound: true, yoodli: false },
  { velyq: true, huru: false, finalRound: false, yoodli: false },
  { velyq: "pricing", huru: "pricing", finalRound: "pricing", yoodli: "pricing" },
];

function renderCell(
  cell: boolean | "pricing",
  pricing: { velyq: string; huru: string; finalRound: string; yoodli: string },
  vendor: "velyq" | "huru" | "finalRound" | "yoodli"
) {
  if (cell === "pricing") {
    if (vendor === "velyq") {
      return <span className="font-semibold text-primary">{pricing[vendor]}</span>;
    }
    return pricing[vendor];
  }
  if (cell === true) {
    return <Check className="w-4 h-4 mx-auto text-green-500" />;
  }
  return <X className="w-4 h-4 mx-auto text-muted-foreground" />;
}

export default function PlateformeIaEntretienPage() {
  const { locale } = useTranslation();
  const content = locale === "fr" ? t.fr : t.en;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link href="/">
            <Logo />
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <LanguageSwitcher />
            <Link href="/login" className="hidden sm:block">
              <Button variant="ghost" size="sm">
                {content.nav.login}
              </Button>
            </Link>
            <Link href="/register">
              <Button size="sm">
                <span className="hidden sm:inline">{content.nav.ctaLong}</span>
                <span className="sm:hidden">{content.nav.ctaShort}</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero — AEO direct answer */}
      <section className="py-16 sm:py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <Badge className="mb-4" variant="outline">
              <Sparkles className="w-3 h-3 mr-1" />
              {content.hero.badge}
            </Badge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 tracking-tight">
              {content.hero.titleLine1}
              <br className="hidden sm:block" /> {content.hero.titleLine2}
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto mb-4">
              {content.hero.description}
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 mt-8">
              <Link href="/register">
                <Button size="lg" className="gap-2">
                  <Zap className="w-4 h-4" />
                  {content.hero.ctaPrimary}
                </Button>
              </Link>
              <Link href="/pricing">
                <Button size="lg" variant="outline" className="gap-2">
                  {content.hero.ctaSecondary}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-6 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Check className="w-3 h-3 text-primary" />
                {content.hero.trustNoCard}
              </span>
              <span className="flex items-center gap-1">
                <Lock className="w-3 h-3 text-primary" />
                {content.hero.trustHosting}
              </span>
              <span className="flex items-center gap-1">
                <Globe className="w-3 h-3 text-primary" />
                {content.hero.trustLang}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi français — angle différenciateur */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <Badge variant="outline" className="mb-3">
              {content.whyFR.badge}
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              {content.whyFR.title}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {content.whyFR.subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {content.whyFR.cards.map((card, i) => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <div className="text-xs font-mono text-primary mb-2">
                    {card.tag}
                  </div>
                  <h3 className="font-semibold mb-2">{card.title}</h3>
                  <p className="text-sm text-muted-foreground">{card.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Fonctionnalités clés */}
      <section className="py-16 sm:py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10">
            <Badge variant="outline" className="mb-3">{content.pillars.badge}</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              {content.pillars.title}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {content.pillars.subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {content.pillars.items.map((pillar, i) => {
              const Icon = pillarIcons[i];
              return (
                <Card key={pillar.title} className="border-2 hover:border-primary/50 transition-colors">
                  <CardContent className="pt-6">
                    <Icon className="w-9 h-9 text-primary mb-4" />
                    <h3 className="font-semibold mb-2">{pillar.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {pillar.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparatif */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <Badge variant="outline" className="mb-3">{content.comparison.badge}</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              {content.comparison.title}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {content.comparison.subtitle}
            </p>
          </div>
          <div className="overflow-x-auto rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">{content.comparison.columns.criterion}</th>
                  <th className="px-4 py-3 text-center font-semibold text-primary">
                    Velyq
                  </th>
                  <th className="px-4 py-3 text-center font-semibold">Huru</th>
                  <th className="px-4 py-3 text-center font-semibold">
                    Final Round AI
                  </th>
                  <th className="px-4 py-3 text-center font-semibold">Yoodli</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRowsBase.map((row, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-4 py-3">{content.comparison.rows[i]}</td>
                    <td className="px-4 py-3 text-center">
                      {renderCell(row.velyq, content.comparison.pricing, "velyq")}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {renderCell(row.huru, content.comparison.pricing, "huru")}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {renderCell(row.finalRound, content.comparison.pricing, "finalRound")}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {renderCell(row.yoodli, content.comparison.pricing, "yoodli")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-4 text-center">
            {content.comparison.ethicsNote}
            <Link
              href="/blog/detecter-ia-entretien-triche"
              className="underline hover:text-foreground"
            >
              {content.comparison.ethicsLink}
            </Link>
          </p>
        </div>
      </section>

      {/* Cas d'usage */}
      <section className="py-16 sm:py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <Badge variant="outline" className="mb-3">{content.useCases.badge}</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              {content.useCases.title}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {content.useCases.items.map((useCase, i) => {
              const Icon = useCaseIcons[i];
              return (
                <Card
                  key={useCase.title}
                  className="border-2 hover:border-primary/50 transition-colors"
                >
                  <CardContent className="pt-6">
                    <Icon className="w-10 h-10 text-primary mb-4" />
                    <h3 className="font-semibold mb-2">{useCase.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {useCase.description}
                    </p>
                    <Link href="/register" className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1">
                      {useCase.cta} <ArrowRight className="w-3 h-3" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Resources — cross-links */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <Badge variant="outline" className="mb-3">{content.resources.badge}</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              {content.resources.title}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {(["entretien-tech", "entretien-finance", "entretien-consulting"] as const).map((href, i) => {
              const card = content.resources.cards[i];
              return (
                <Link key={href} href={`/${href}`} className="group">
                  <Card className="h-full hover:border-primary/50 transition-colors">
                    <CardContent className="pt-6">
                      <div className="text-xs font-mono text-primary mb-3">
                        {card.tag}
                      </div>
                      <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">
                        {card.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        {card.desc}
                      </p>
                      <span className="text-xs font-mono text-primary">{card.cta}</span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-3 justify-center pt-8 border-t border-border mt-10">
            <span className="text-xs text-muted-foreground font-mono self-center mr-2">
              {content.resources.alsoLabel}
            </span>
            <Link href="/analyse-cv">
              <Badge variant="outline" className="hover:border-primary cursor-pointer">
                {content.resources.atsBadge}
              </Badge>
            </Link>
            <Link href="/blog/ia-entretien-embauche">
              <Badge variant="outline" className="hover:border-primary cursor-pointer">
                {content.resources.aiBadge}
              </Badge>
            </Link>
            <Link href="/blog/alternative-interview-warmup">
              <Badge variant="outline" className="hover:border-primary cursor-pointer">
                {content.resources.altWarmupBadge}
              </Badge>
            </Link>
            <Link href="/blog">
              <Badge variant="outline" className="hover:border-primary cursor-pointer">
                {content.resources.blogBadge}
              </Badge>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            {content.cta.title}
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            {content.cta.subtitle}
          </p>
          <Link href="/register">
            <Button size="lg" className="gap-2">
              {content.cta.button}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
          <p className="text-xs text-muted-foreground mt-4">
            {content.cta.disclaimer}
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
